import { concat, fn } from '@ember/helper';
import { action } from '@ember/object';
import { getOwner } from '@ember/owner';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import {
  PrismButton,
  PrismComboSelectInput,
  PrismInput,
  PrismModal,
} from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { not, or } from 'ember-truth-helpers';
import type { Account } from 'frontend-payments/models';
import { getBillingStatesByCountry } from 'frontend-payments/utils/constants/billing-states';
import Countries, { type CountryCode } from 'frontend-utils/utils/countries';

import styles from './modal-edit-billing-details.module.css';

export interface BillingFormData {
  city: string;
  country: CountryCode | '';
  legalEntityName: string;
  state: string | null;
  street: string;
  vatNumber: string;
  zipCode: string;
}

export type BillingFormField = 'vatNumber';

export type BillingDetailsValidationErrors = Partial<
  Record<BillingFormField, string[]>
>;

interface CountrySelectInput {
  id: CountryCode;
  label: string;
}

interface StateSelectInput {
  id: string;
  label: string;
}

interface PaymentModalEditBillingDetailsSignature {
  Args: {
    account: Account;
    isUpdating: boolean;
    onCloseModal: () => void;
    onUpdateBillingDetails: (data: BillingFormData) => Promise<void>;
    validationErrors?: BillingDetailsValidationErrors;
  };
}

export default class PaymentModalEditBillingDetails extends Component<PaymentModalEditBillingDetailsSignature> {
  @tracked billingData: BillingFormData = {
    city: this.args.account?.billing?.city || '',
    country: this.args.account?.billing?.country || '',
    legalEntityName: this.args.account?.billing?.legalEntityName || '',
    state: this.args.account?.billing?.state || '',
    street: this.args.account?.billing?.street || '',
    vatNumber: this.args.account?.billing?.vatNumber || '',
    zipCode: this.args.account?.billing?.zipCode || '',
  };

  private countries: Countries = new Countries(getOwner(this)!);

  @cached
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get countryOptions() {
    return this.countries.list
      .map((country) => ({
        id: country.id,
        label: country.name,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isFormValid() {
    const { city, country, legalEntityName, state, street, zipCode } =
      this.billingData;

    const requiredFields = [city, country, legalEntityName, street, zipCode];

    // Only require state for US and Canada
    if (this.requiresBillingState && state) {
      requiredFields.push(state);
    }

    const hasAllRequiredFields = requiredFields.every(
      (field) => field.trim() !== '',
    );

    // For US/CA countries, also ensure state is provided and not null
    if (this.requiresBillingState) {
      return hasAllRequiredFields && state && state.trim() !== '';
    }

    return hasAllRequiredFields;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get requiresBillingState() {
    return (
      this.billingData.country === 'US' || this.billingData.country === 'CA'
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get selectedCountry() {
    return (
      this.countryOptions.find(
        (countryOption) => countryOption.id === this.billingData.country,
      ) || null
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get selectedState() {
    if (!this.billingData.state) {
      return null;
    }

    return (
      this.stateOptions.find(
        (stateOption) => stateOption.id === this.billingData.state,
      ) || null
    );
  }

  @cached
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get stateOptions() {
    if (!this.billingData.country) {
      return [];
    }

    const states = getBillingStatesByCountry(this.billingData.country);
    return states.map((state) => ({
      id: state.code,
      label: state.name,
    }));
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  countrySearchMatcher(countryOption: CountrySelectInput, searchTerm: string) {
    // Only show countries that actually start with the search term
    return countryOption.label
      .toLowerCase()
      .startsWith(searchTerm.toLowerCase())
      ? 1
      : -1;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  stateSearchMatcher(stateOption: StateSelectInput, searchTerm: string) {
    return stateOption.label.toLowerCase().startsWith(searchTerm.toLowerCase())
      ? 1
      : -1;
  }

  @action
  getFieldError(field: BillingFormField): string | undefined {
    if (!this.args.validationErrors) {
      return undefined;
    }

    const errors = this.args.validationErrors[field];
    return errors?.[0];
  }

  @action
  onCountryChange(countryOption: CountrySelectInput): void {
    this.billingData = {
      ...this.billingData,
      country: countryOption.id,
      // Reset state when country changes - set to null for non-US/CA countries
      state: countryOption.id === 'US' || countryOption.id === 'CA' ? '' : null,
    };
  }

  @action
  onInput(field: keyof BillingFormData, event: Event): void {
    const target = event.target as HTMLInputElement;
    this.billingData = {
      ...this.billingData,
      [field]: target.value,
    };
  }

  @action
  onStateChange(stateOption: StateSelectInput): void {
    this.billingData = {
      ...this.billingData,
      state: stateOption.id,
    };
  }

  @action
  async submitBillingDetails(): Promise<void> {
    if (!this.isFormValid) {
      return;
    }

    // For non-US/CA countries, set state to null
    const dataToSubmit = {
      ...this.billingData,
      state: this.requiresBillingState ? this.billingData.state : null,
    };

    await this.args.onUpdateBillingDetails(dataToSubmit);
  }

  <template>
    <PrismModal
      @onClose={{@onCloseModal}}
      @title={{t "payments.billing_details.title"}}
      data-test-id-modal-edit-billing-details
    >
      <:default>
        <div class="modal--small modal--padded">
          <div class="flex w-full">
            <PrismInput
              @label={{t "payments.billing_details.legal_name"}}
              @onInput={{fn this.onInput "legalEntityName"}}
              @required={{true}}
              @requiredErrorMessage={{t "general.required"}}
              @value={{this.billingData.legalEntityName}}
            />
          </div>

          <div class="flex w-full">
            <PrismInput
              @label={{t "payments.billing_details.address"}}
              @onInput={{fn this.onInput "street"}}
              @required={{true}}
              @requiredErrorMessage={{t "general.required"}}
              @value={{this.billingData.street}}
            />
          </div>

          <div class="flex gap-400">
            <div class="flex w-1/5">
              <PrismInput
                @label={{t "payments.billing_details.zip"}}
                @onInput={{fn this.onInput "zipCode"}}
                @required={{true}}
                @requiredErrorMessage={{t "general.required"}}
                @value={{this.billingData.zipCode}}
              />
            </div>
            <div class="flex w-4/5">
              <PrismInput
                @label={{t "payments.billing_details.city"}}
                @onInput={{fn this.onInput "city"}}
                @required={{true}}
                @requiredErrorMessage={{t "general.required"}}
                @value={{this.billingData.city}}
              />
            </div>
          </div>

          <div class="flex gap-400">
            <div class="mb-400 flex w-full flex-col gap-100">
              <p class="text-200--bold">
                {{t "payments.billing_details.country"}}
                <span>*</span>
              </p>
              <PrismComboSelectInput
                @isSearchable={{true}}
                @isSecondary={{true}}
                @onChange={{this.onCountryChange}}
                @options={{this.countryOptions}}
                @searchMatcher={{this.countrySearchMatcher}}
                @unsafe_matchTriggerWidth={{true}}
                @value={{this.selectedCountry}}
              />
            </div>
            {{#if this.requiresBillingState}}
              <div class="mb-400 flex w-full flex-col gap-100">
                <p class="text-200--bold">
                  {{t "payments.billing_details.state"}}
                  <span>*</span>
                </p>
                <PrismComboSelectInput
                  @isSearchable={{true}}
                  @isSecondary={{true}}
                  @onChange={{this.onStateChange}}
                  @options={{this.stateOptions}}
                  @searchMatcher={{this.stateSearchMatcher}}
                  @unsafe_matchTriggerWidth={{true}}
                  @value={{this.selectedState}}
                />
              </div>
            {{/if}}
          </div>

          <div class="flex w-full">
            <PrismInput
              @errorMessage={{this.getFieldError "vatNumber"}}
              @hasErrors={{if (this.getFieldError "vatNumber") true false}}
              @isTouched={{if (this.getFieldError "vatNumber") true}}
              @label={{t "payments.billing_details.vat"}}
              @onInput={{fn this.onInput "vatNumber"}}
              @requiredErrorMessage={{t "general.required"}}
              @value={{this.billingData.vatNumber}}
              data-test-id-vat-number-input
            />
          </div>
        </div>
      </:default>

      <:footer>
        <div class={{concat styles.footer " " "flex justify-end"}}>
          <PrismButton
            @buttonType="secondary"
            @isDisabled={{@isUpdating}}
            @label={{t "general.cancel"}}
            @onClick={{@onCloseModal}}
          />
          <PrismButton
            @isDisabled={{or (not this.isFormValid) @isUpdating}}
            @isLoading={{@isUpdating}}
            @label={{t "general.save"}}
            @onClick={{this.submitBillingDetails}}
          />
        </div>
      </:footer>
    </PrismModal>
  </template>
}
